import styled, { css } from 'styled-components';
import { Price as LibPrice, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { useColumn } from '../columns/column-context';
import { HORIZONTAL_PADDING, VERTICAL_PADDING } from '../constants';
import { getAlignment, getDiscount } from '../utils';
import { ColumnElementBase } from './column-element-base';
import { type ColumnElement } from './column-elements';

export function Price({
  className,
  $alignRows: alignRows,
  $columnIndex: columnIndex,
  $rowIndex: rowIndex,
  $additionalTopPadding: additionalTopPadding
}: ColumnElement) {
  const { t } = useLingui();
  const { language } = useSettings();
  const { language: locale } = language;

  const { discount, oldPrice, price, priceCaption } = useColumn();
  const { pricingModel } = price;
  const { enabled: oldPriceEnabled } = oldPrice;
  const { discountType } = discount;
  const shouldShowOldPrice = oldPriceEnabled && pricingModel !== 'free';
  const derivedPricingModel =
    pricingModel === 'from' && shouldShowOldPrice ? 'fixed' : pricingModel;

  const discountValue = getDiscount({
    discount,
    newPrice: price,
    oldPrice,
    locale
  });
  const discountText =
    discountType === 'custom' || !discountValue
      ? discountValue
      : t`Save ${discountValue}`;

  const forwardingProps = {
    $alignRows: alignRows,
    $columnIndex: columnIndex
  };

  return (
    <Container
      className={className}
      $alignRows={alignRows}
      $additionalTopPadding={additionalTopPadding}
    >
      {alignRows && (
        <PaddingBlock
          {...forwardingProps}
          $rowIndex={rowIndex}
          $withElementClass={false}
          $additionalTopPadding={additionalTopPadding}
        />
      )}

      <ValueContainer
        {...forwardingProps}
        $rowIndex={rowIndex + 1}
        $withElementClass={alignRows}
      >
        {shouldShowOldPrice && (
          <StyledLibOldPrice language={language} {...price} {...oldPrice} />
        )}

        <StyledLibPrice
          {...price}
          language={language}
          pricingModel={derivedPricingModel}
        />
      </ValueContainer>

      {(!!discountText || !!priceCaption) && (
        <DetailsContainer
          {...forwardingProps}
          $rowIndex={rowIndex + 2}
          $withElementClass={alignRows}
        >
          {!!discountText && (
            <Discount $alignRows={alignRows}>{discountText}</Discount>
          )}

          {!!priceCaption && (
            <Caption dangerouslySetInnerHTML={{ __html: priceCaption }} />
          )}
        </DetailsContainer>
      )}

      {alignRows && (
        <PaddingBlock
          {...forwardingProps}
          $rowIndex={rowIndex + 3}
          $withElementClass={false}
        />
      )}
    </Container>
  );
}

interface ContainerProps {
  $alignRows: boolean;
  $additionalTopPadding?: number;
}

const Container = styled.div.attrs<ContainerProps>(({ $alignRows }) => ({
  className: createReadableClass({ component: 'price', element: 'container' }, [
    !$alignRows && 'es-column-element'
  ])
}))`
  position: relative;
  flex-direction: column;
  padding: ${({ $additionalTopPadding = 0 }) =>
      VERTICAL_PADDING + $additionalTopPadding}px
    ${HORIZONTAL_PADDING}px ${VERTICAL_PADDING}px;
  display: ${({ $alignRows }) => ($alignRows ? 'contents' : 'flex')};

  ${({
    theme: {
      price: { alignment }
    }
  }) => css`
    text-align: ${alignment};
    align-items: ${getAlignment(alignment)};
  `}
`;

const PaddingBlock = styled(ColumnElementBase).attrs({
  className: createReadableClass({
    component: 'price',
    element: 'padding-block'
  })
})`
  padding-top: ${({ $additionalTopPadding = 0 }) =>
    VERTICAL_PADDING + $additionalTopPadding}px;
`;

const ValueContainer = styled(ColumnElementBase).attrs({
  className: createReadableClass({
    component: 'price',
    element: 'value-container'
  })
})`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: end;

  ${({ $alignRows }) =>
    $alignRows &&
    css`
      padding: 0 ${HORIZONTAL_PADDING}px;
    `}
`;

const StyledBasePrice = styled(LibPrice).attrs({ isVertical: true })`
  justify-content: ${({
    theme: {
      price: { alignment }
    }
  }) => alignment};
`;

const StyledLibOldPrice = styled(StyledBasePrice).attrs(
  ({
    theme: {
      price: { oldPriceColor, oldPriceFont }
    }
  }) => ({
    valueColor: oldPriceColor,
    valueFont: oldPriceFont,
    className: createReadableClass({ component: 'old-price' }),
    strikethrough: true
  })
)`
  margin-bottom: 4px;
`;

const StyledLibPrice = styled(StyledBasePrice).attrs(
  ({
    theme: {
      price: { captionColor, captionFont, color, font }
    }
  }) => ({
    valueColor: color,
    valueFont: font,
    affixColor: captionColor,
    affixFont: captionFont,
    className: createReadableClass({ component: 'price' })
  })
)``;

const DetailsContainer = styled(ColumnElementBase).attrs({
  className: createReadableClass({
    component: 'price',
    element: 'details-container'
  })
})`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: ${({
    theme: {
      price: { alignment }
    }
  }) => getAlignment(alignment)};

  ${({ $alignRows }) =>
    $alignRows &&
    css`
      padding: 0 ${HORIZONTAL_PADDING}px;
    `}
`;

const Discount = styled(Typography).attrs<{ $alignRows: boolean }>(
  ({
    theme: {
      price: { discountFont, discountTextColor }
    }
  }) => ({
    color: discountTextColor,
    textStyle: {
      ...discountFont,
      fontWeight:
        discountFont?.fontWeight === 'normal' ? 600 : discountFont?.fontWeight
    },
    className: createReadableClass({ component: 'price', element: 'discount' })
  })
)`
  max-width: 100%;
`;

const Caption = styled(Typography).attrs(
  ({
    theme: {
      price: { captionColor, captionFont }
    }
  }) => ({
    textStyle: captionFont,
    color: captionColor,
    className: createReadableClass({ component: 'price', element: 'caption' })
  })
)``;
